import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Paragraph } from "@fronterahealth/frontera-ui-components";

import { PassageGoalType } from "@api/graphql/types-and-hooks";
import { ProgrammingGoalReviewHeader } from "@components/ProgrammingReview/ProgrammingGoalReviewHeader";
import { ProgrammingReviewGoalCard } from "@components/ProgrammingReview/ProgrammingReviewGoalCard";
import { useProgrammingReviewTargets } from "@components/ProgrammingReview/hooks/useProgrammingReviewTargets";
import { DeleteTargetConfirmDialog } from "@pages/ClientProgramming/ProgrammingReview/components/DeleteTargetConfirmDialog";
import { ProgramReviewGoalList, useProgrammingReviewData } from "@providers/ProgrammingReviewProvider";

interface ProgrammingReviewLeftPanelProps {
  isReadOnly?: boolean;
}

export const ProgrammingReviewLeftPanel: React.FC<ProgrammingReviewLeftPanelProps> = ({ isReadOnly = false }) => {
  const { clientId } = useParams();

  const {
    programReviewGoalList,
    searchGoalText,
    selectedProgramReviewTarget,
    getProgramReviewGoalsQuery: { isFetching, refetch: refetchProgramReviewGoals },
  } = useProgrammingReviewData();

  // Target operations hook
  const {
    handleSelectGoal,
    handleSelectTarget,
    handleDeleteTarget,
    handleOpenCustomTargetForm,
    handleOpenSuggestedTargetForm,
    isDeleteConfirmDialogOpen,
    setIsDeleteConfirmDialogOpen,
    targetToDelete,
    confirmDeleteTarget,
    cancelDeleteTarget,
    isDeletingTarget,
  } = useProgrammingReviewTargets({
    clientId,
    programReviewGoalList,
    refetchProgramReviewGoals,
  });

  const [collapseAll, setCollapseAll] = useState(false);
  const [filteredProgramReviewGoalList, setFilteredProgramReviewGoalList] = useState<ProgramReviewGoalList>([]);

  const selectedTargetId = selectedProgramReviewTarget?.id ?? null;

  useEffect(() => {
    if (!programReviewGoalList?.length) return;

    const trimmedSearch = searchGoalText.trim().toLowerCase();

    if (!trimmedSearch) {
      setFilteredProgramReviewGoalList(programReviewGoalList);
      return;
    }

    const goals: ProgramReviewGoalList = [];

    programReviewGoalList.forEach((goal) => {
      const goalMatches = goal.title?.toLowerCase().includes(trimmedSearch);
      const hasMatchingTargets =
        goal.passageTargets?.edges?.some((t) => t?.node?.title?.toLowerCase().includes(trimmedSearch)) || false;

      if (goalMatches || hasMatchingTargets) {
        goals.push(goal);
      }
    });
    setFilteredProgramReviewGoalList(goals);
  }, [searchGoalText, programReviewGoalList]);

  const renderGoalsList = () => {
    if (isFetching && !programReviewGoalList) {
      return (
        <div className="flex justify-center items-center py-12">
          <Paragraph size="large" colorType="primary" displayType="loud">
            Loading...
          </Paragraph>
        </div>
      );
    }

    return filteredProgramReviewGoalList?.map((goal) => (
      <ProgrammingReviewGoalCard
        key={goal.id}
        goal={goal as PassageGoalType}
        collapseAll={collapseAll}
        selectedTargetId={selectedTargetId}
        onAddCustomTarget={() => handleOpenCustomTargetForm(goal.id)}
        onAddSuggestedTarget={() => handleOpenSuggestedTargetForm(goal.id)}
        onSelectTarget={handleSelectTarget}
        onSelectGoal={handleSelectGoal}
        onDeleteTarget={handleDeleteTarget}
        isReadOnly={isReadOnly}
      />
    ));
  };

  return (
    <div className="w-[25vw] px-2 py-4 pt-0 bg-white">
      <ProgrammingGoalReviewHeader
        goalList={programReviewGoalList || []}
        collapseAll={collapseAll}
        setCollapseAll={setCollapseAll}
        isReadOnly={isReadOnly}
      />
      <div className="overflow-y-auto max-h-[calc(100vh-14.75rem)] mt-2 min-h-[calc(100vh-14.75rem)]">
        {renderGoalsList()}
      </div>
      <DeleteTargetConfirmDialog
        isOpen={isDeleteConfirmDialogOpen}
        setIsOpen={setIsDeleteConfirmDialogOpen}
        targetTitle={targetToDelete?.title ?? ""}
        isDeleting={isDeletingTarget}
        onConfirm={confirmDeleteTarget}
        onCancel={cancelDeleteTarget}
      />
    </div>
  );
};
